import * as ReactTable from "@tanstack/react-table"
import { clamp } from "lodash"
import * as React from "react"
import { useTranslation } from "react-i18next"

import { IVariant } from "shared/hubrise/models"
import { snakeCaseFormat } from "shared/utils/format"
import { nthPercentile } from "shared/utils/javascript"

import { PublicRowProps } from "./Row"
import { RowData } from "./types"

const standardSize = 160
const minSize = 60
const charSize = 8
const enabledSize = 0.8 * standardSize
const priceSize = 0.7 * standardSize
const minVariantSize = 1.2 * standardSize

export const useColumns = <ItemType extends { id: number }>(
  rowProps: PublicRowProps<ItemType>,
  designatorsHeaders: Array<string>,
  removeAllRefs: () => void,
  openGenerateRefs: () => void,
  variants: Array<IVariant>,
  enableAll: (enabled: boolean, variantRef?: string) => void,
  bulkUpdatePrices: ((variant: IVariant | null) => void) | undefined,
  clearAllPrices: ((variant: IVariant) => void) | undefined,
  items: Array<ItemType>,
): {
  groupColumns: Array<ReactTable.GroupColumnDef<RowData<ItemType>>>
  columns: Array<ReactTable.ColumnDef<RowData<ItemType>>>
} => {
  const { t } = useTranslation()

  type GroupColumn = ReactTable.GroupColumnDef<RowData<ItemType>>
  type Column = ReactTable.ColumnDef<RowData<ItemType>>

  const groupColumns = React.useMemo<Array<GroupColumn>>(() => {
    const initialSize = (nbChars: (item: ItemType) => number): number => {
      const length = nthPercentile(items.map(nbChars), 75)
      return clamp(length * charSize, standardSize, standardSize * 2)
    }

    return [
      {
        header: "",
        id: "designators",
        meta: {
          columnType: { type: "placeholder_group" },
          stickyIndex: 0,
          isLastSticky: true,
        },
        columns: [
          ...designatorsHeaders.map(
            (designator, index) =>
              ({
                accessorKey: snakeCaseFormat(designator),
                header: designator,
                size: initialSize((item) => rowProps.designators(item)[index].length),
                minSize,
                meta: {
                  columnType: { type: "designator", index },
                  stickyIndex: index,
                },
              }) satisfies Column,
          ),
          {
            accessorKey: "ref_code",
            header: t("grid.ref"),
            size: initialSize((item) => rowProps.itemRef(item).length),
            minSize,
            meta: {
              columnType: { type: "ref_code" },
              stickyIndex: designatorsHeaders.length,
              isLastSticky: true,
              actions: [
                {
                  label: t("grid.actions.remove_all_refs"),
                  onClick: removeAllRefs,
                },
                {
                  label: t("grid.actions.generate_refs"),
                  onClick: openGenerateRefs,
                },
              ],
            },
          } satisfies Column,
        ],
      } satisfies GroupColumn,
      {
        header: t("grid.default"),
        id: "default",
        meta: { columnType: { type: "variant_group" }, center: true },
        columns: [
          {
            accessorKey: "enabled",
            header: t("grid.enabled"),
            size: rowProps.variantPrice ? enabledSize : minVariantSize,
            minSize,
            meta: {
              columnType: { type: "enabled", variantRef: null },
              center: true,
              actions: [
                {
                  label: t("grid.actions.enable_all"),
                  onClick: () => enableAll(true),
                },
                {
                  label: t("grid.actions.disable_all"),
                  onClick: () => enableAll(false),
                },
              ],
            },
          } satisfies Column,
          ...(rowProps.variantPrice
            ? [
                {
                  accessorKey: "price",
                  header: t("grid.price"),
                  size: priceSize,
                  minSize,
                  meta: {
                    columnType: { type: "price", variantRef: null },
                    center: true,
                    actions: [
                      ...(bulkUpdatePrices
                        ? [
                            {
                              label: t("grid.actions.update_all"),
                              onClick: () => bulkUpdatePrices(null),
                            },
                          ]
                        : []),
                    ],
                  },
                } satisfies Column,
              ]
            : []),
        ],
      } satisfies GroupColumn,
      ...variants.map(
        (variant) =>
          ({
            header: variant.name,
            id: `v${variant.id}`,
            meta: { columnType: { type: "variant_group" }, center: true },
            columns: [
              {
                accessorKey: `v${variant.id}_enabled`,
                header: t("grid.enabled"),
                size: rowProps.variantPrice ? enabledSize : minVariantSize,
                minSize,
                meta: {
                  columnType: { type: "enabled", variantRef: variant.ref },
                  center: true,
                  actions: [
                    {
                      label: t("grid.actions.enable_all"),
                      onClick: () => enableAll(true, variant.ref),
                    },
                    {
                      label: t("grid.actions.disable_all"),
                      onClick: () => enableAll(false, variant.ref),
                    },
                  ],
                },
              } satisfies Column,
              ...(rowProps.variantPrice
                ? [
                    {
                      accessorKey: `v${variant.id}_price`,
                      header: t("grid.price"),
                      size: priceSize,
                      minSize,
                      meta: {
                        columnType: { type: "price", variantRef: variant.ref },
                        center: true,
                        actions: [
                          ...(bulkUpdatePrices
                            ? [
                                {
                                  label: t("grid.actions.update_all"),
                                  onClick: () => bulkUpdatePrices(variant),
                                },
                              ]
                            : []),
                          ...(clearAllPrices
                            ? [
                                {
                                  label: t("grid.actions.clear_all"),
                                  onClick: () => clearAllPrices(variant),
                                },
                              ]
                            : []),
                        ],
                      },
                    } satisfies Column,
                  ]
                : []),
            ],
          }) satisfies GroupColumn,
      ),
    ]
  }, [
    designatorsHeaders,
    t,
    rowProps,
    bulkUpdatePrices,
    variants,
    items,
    enableAll,
    clearAllPrices,
    removeAllRefs,
    openGenerateRefs,
  ])

  return {
    groupColumns,
    columns: groupColumns.flatMap((groupColumn) => groupColumn.columns!),
  }
}

export const useData = <ItemType extends { id: number }>(
  items: Array<ItemType>,
  rowProps: PublicRowProps<ItemType>,
  designatorsHeaders: Array<string>,
  variants: Array<IVariant>,
) => {
  return React.useMemo(() => {
    return items.map((item) => {
      const row: RowData<ItemType> = { ref_code: rowProps.itemRef(item), item }

      designatorsHeaders.forEach((designator, index) => {
        const designatorKey = snakeCaseFormat(designator)
        row[designatorKey] = rowProps.designators(item)[index]
      })

      row["enabled"] = rowProps.isEnabled(item)
      if (rowProps.variantPrice) {
        row["price"] = rowProps.catalogPrice(item).cents
      }

      variants.forEach((variant) => {
        row[`v${variant.id}_enabled`] = rowProps.isEnabled(item, variant.ref)
        if (rowProps.variantPrice) {
          row[`v${variant.id}_price`] =
            rowProps.variantPrice(item, variant.ref)?.cents ?? rowProps.catalogPrice(item).cents
        }
      })

      return row
    })
  }, [items, rowProps, designatorsHeaders, variants])
}
